import React, { useState, useEffect } from 'react';
import { 
  FiUser, FiPhone, FiMapPin, FiCalendar, FiAward, FiUsers, 
  FiFileText, FiGift, FiHash, FiInstagram, FiFacebook, 
  FiTrendingUp, FiBook, FiMessageSquare
} from 'react-icons/fi';
import { FaLine, FaTiktok } from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';
import TrainingManager from '../common/TrainingManager';
import PolicyManager from '../common/PolicyManager';
import CoachingManager from '../common/CoachingManager';
import ProfileSection from '../common/ProfileSection';
import { AGENT_STATUS_OPTIONS, getAgentStatusMeta } from '../../utils/agentStatus';

const AgentProfileDetail = ({ profile, onSave, readOnly = false }) => {
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (profile) {
      setFormData(profile);
    }
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEdit = (section) => {
    setFormData(profile); // Reset form data to match current profile before editing
    setEditingSection(section);
  };

  const handleCancel = () => {
    setEditingSection(null);
    setFormData(profile);
  };

  const handleSectionSave = async () => {
    if (onSave) {
      const success = await onSave(formData);
      if (success) {
        setEditingSection(null);
      }
    } else {
        setEditingSection(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  if (!profile) return null;

  const userImageUrl = profile.user?.pictureUrl;
  const fullName = `${profile.firstName} ${profile.lastName}`;
  const downlineCount = profile.downlines?.length || 0;
  const policyCount = profile.policies?.length || 0;
  // Calculate team policies if downlines exist
  const teamPolicyCount = profile.downlines?.reduce((sum, downline) => sum + (downline.policies?.length || 0), 0) || 0;
  const totalPolicies = policyCount + teamPolicyCount;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-100 flex items-center">
         <div className="flex-shrink-0 h-20 w-20">
            {userImageUrl ? (
              <img className="h-20 w-20 rounded-full object-cover border-4 border-indigo-50" src={userImageUrl} alt={fullName} />
            ) : (
              <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center border-4 border-indigo-50">
                <SafeIcon icon={FiUser} className="h-10 w-10 text-indigo-500" />
              </div>
            )}
         </div>
         <div className="ml-4">
            <h3 className="text-xl font-bold text-gray-900">{fullName}</h3>
            <p className="text-indigo-600 font-medium">{profile.position || 'ตัวแทน'}</p>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <SafeIcon icon={FiHash} className="mr-1 h-3 w-3" />
              {profile.agentCode || '-'}
            </div>
         </div>
      </div>

      {/* Stats Section */}
       <div className="grid grid-cols-2 gap-2 sm:gap-4">
         {/* <div className="bg-white p-2 sm:p-6 rounded-lg shadow flex flex-col sm:flex-row items-start sm:items-center justify-between border-l-4 border-indigo-500">
           <div className="overflow-hidden w-full">
             <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0 sm:mb-1 truncate">ลูกทีมทั้งหมด</p>
             <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{downlineCount} คน</p>
           </div>
           <div className="hidden sm:block bg-indigo-100 p-3 rounded-full flex-shrink-0">
              <SafeIcon icon={FiUsers} className="w-6 h-6 text-indigo-600" />
           </div>
         </div> */}
         <div className="bg-white p-2 sm:p-6 rounded-lg shadow flex flex-col sm:flex-row items-start sm:items-center justify-between border-l-4 border-green-500">
           <div className="overflow-hidden w-full">
             <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0 sm:mb-1 truncate">กรมธรรม์ส่วนตัว</p>
             <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{policyCount} ฉบับ</p>
           </div>
           <div className="hidden sm:block bg-green-100 p-3 rounded-full flex-shrink-0">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-green-600" />
           </div>
         </div>
       </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* 1. General Info */}
      <ProfileSection 
        title="ข้อมูลทั่วไป" 
        icon={FiUser} 
        isEditing={editingSection === 'general'}
        onEdit={() => handleEdit('general')}
        onSave={handleSectionSave}
        onCancel={handleCancel}
        hideEdit={readOnly}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ชื่อจริง (ไทย)</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">นามสกุล (ไทย)</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.lastName}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ชื่อจริง (อังกฤษ)</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="firstNameEn"
                  value={formData.firstNameEn || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.firstNameEn || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">นามสกุล (อังกฤษ)</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="lastNameEn"
                  value={formData.lastNameEn || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.lastNameEn || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ชื่อเล่น</label>
              {editingSection === 'general' ? (
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{profile.nickname || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">วันเกิด</label>
              {editingSection === 'general' ? (
                <input
                  type="date"
                  name="birthDate"
                  value={formatDateForInput(formData.birthDate)}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <div className="flex items-center">
                  <SafeIcon icon={FiGift} className="w-3 h-3 text-gray-400 mr-2" />
                  <p className="text-sm font-medium text-gray-900">{formatDate(profile.birthDate)}</p>
                </div>
              )}
            </div>
        </div>
      </ProfileSection>

      {/* 2. Contact Info */}
      <ProfileSection 
        title="การติดต่อ" 
        icon={FiPhone} 
        isEditing={editingSection === 'contact'}
        onEdit={() => handleEdit('contact')}
        onSave={handleSectionSave}
        onCancel={handleCancel}
        hideEdit={readOnly}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">เบอร์โทรศัพท์</label>
            {editingSection === 'contact' ? (
              <input
                type="tel"
                name="phone"
                value={formData.phone || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <div className="flex items-center">
                <SafeIcon icon={FiPhone} className="w-3 h-3 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-900">{profile.phone || '-'}</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Line ID</label>
            {editingSection === 'contact' ? (
              <input
                type="text"
                name="lineId"
                value={formData.lineId || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <div className="flex items-center">
                <SafeIcon icon={FaLine} className="w-3 h-3 text-green-500 mr-2" />
                <p className="text-sm font-medium text-gray-900">{profile.lineId || '-'}</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">อีเมล์</label>
            {editingSection === 'contact' ? (
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <p className="text-sm font-medium text-gray-900">{profile.email || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">พื้นที่รับผิดชอบ</label>
            {editingSection === 'contact' ? (
              <input
                type="text"
                name="area"
                value={formData.area || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <div className="flex items-center">
                <SafeIcon icon={FiMapPin} className="w-3 h-3 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-900">{profile.area || '-'}</p>
              </div>
            )}
          </div>
        </div>
      </ProfileSection>

      {/* 3. Work & License */}
      <ProfileSection 
        title="ข้อมูลงานและใบอนุญาต" 
        icon={FiAward} 
        isEditing={editingSection === 'work'}
        onEdit={() => handleEdit('work')}
        onSave={handleSectionSave}
        onCancel={handleCancel}
        hideEdit={readOnly}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">ตำแหน่ง</label>
                {editingSection === 'work' ? (
                  <input
                    type="text"
                    name="position"
                    value={formData.position || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{profile.position || '-'}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">รหัสหน่วย</label>
                {editingSection === 'work' ? (
                  <input
                    type="text"
                    name="unitCode"
                    value={formData.unitCode || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{profile.unitCode || '-'}</p>
                )}
             </div>
             <div className="col-span-full">
                <label className="block text-xs font-medium text-gray-500 mb-1">สถานภาพตัวแทน</label>
                {editingSection === 'work' ? (
                  <select
                    name="agentStatus"
                    value={getAgentStatusMeta(formData.agentStatus).status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {AGENT_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm font-medium text-gray-900">{getAgentStatusMeta(profile.agentStatus).label}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">วันที่เข้าร่วมงาน</label>
                {editingSection === 'work' ? (
                  <input
                    type="date"
                    name="joinDate"
                    value={formatDateForInput(formData.joinDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="w-3 h-3 text-gray-400 mr-2" />
                    <p className="text-sm font-medium text-gray-900">{formatDate(profile.joinDate)}</p>
                  </div>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">วันที่ตำแหน่งมีผลบังคับ</label>
                {editingSection === 'work' ? (
                  <input
                    type="date"
                    name="positionEffectiveDate"
                    value={formatDateForInput(formData.positionEffectiveDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="w-3 h-3 text-gray-400 mr-2" />
                    <p className="text-sm font-medium text-gray-900">{formatDate(profile.positionEffectiveDate)}</p>
                  </div>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">เลขที่ใบอนุญาตตัวแทน</label>
                {editingSection === 'work' ? (
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{profile.licenseNumber || '-'}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">ระดับใบอนุญาต</label>
                {editingSection === 'work' ? (
                  <input
                    type="text"
                    name="licenseLevel"
                    value={formData.licenseLevel || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{profile.licenseLevel || '-'}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">วันที่เริ่มใบอนุญาต</label>
                {editingSection === 'work' ? (
                  <input
                    type="date"
                    name="licenseStartDate"
                    value={formatDateForInput(formData.licenseStartDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{formatDate(profile.licenseStartDate)}</p>
                )}
             </div>
             <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">วันหมดอายุใบอนุญาต</label>
                {editingSection === 'work' ? (
                  <input
                    type="date"
                    name="licenseExpiryDate"
                    value={formatDateForInput(formData.licenseExpiryDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">{formatDate(profile.licenseExpiryDate)}</p>
                )}
             </div>
        </div>
      </ProfileSection>

      {/* 4. Social & Interests */}
      <ProfileSection 
        title="โซเชียลมีเดียและความสนใจ" 
        icon={FiInstagram} 
        isEditing={editingSection === 'social'}
        onEdit={() => handleEdit('social')}
        onSave={handleSectionSave}
        onCancel={handleCancel}
        hideEdit={readOnly}
      >
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Facebook</label>
                {editingSection === 'social' ? (
                  <input
                    type="text"
                    name="facebook"
                    value={formData.facebook || ''}
                    onChange={handleInputChange}
                    placeholder="Link or Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                     <SafeIcon icon={FiFacebook} className="w-3 h-3 text-blue-600 mr-2" />
                     <p className="text-sm font-medium text-gray-900">{profile.facebook || '-'}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">TikTok</label>
                {editingSection === 'social' ? (
                  <input
                    type="text"
                    name="tiktok"
                    value={formData.tiktok || ''}
                    onChange={handleInputChange}
                     placeholder="Link or Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                     <SafeIcon icon={FaTiktok} className="w-3 h-3 text-black mr-2" />
                     <p className="text-sm font-medium text-gray-900">{profile.tiktok || '-'}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Instagram</label>
                {editingSection === 'social' ? (
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram || ''}
                    onChange={handleInputChange}
                     placeholder="Link or Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <div className="flex items-center">
                     <SafeIcon icon={FiInstagram} className="w-3 h-3 text-pink-600 mr-2" />
                     <p className="text-sm font-medium text-gray-900">{profile.instagram || '-'}</p>
                  </div>
                )}
              </div>
              
              <div className="col-span-full">
                 <label className="block text-xs font-medium text-gray-500 mb-1">แรงจูงใจ / เป้าหมาย</label>
                 {editingSection === 'social' ? (
                  <textarea
                    name="motivation"
                    value={formData.motivation || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                 ) : (
                   <p className="text-sm font-medium text-gray-900">{profile.motivation || '-'}</p>
                 )}
              </div>
               <div className="col-span-full">
                 <label className="block text-xs font-medium text-gray-500 mb-1">ความสนใจ</label>
                 {editingSection === 'social' ? (
                  <textarea
                    name="interest"
                    value={formData.interest || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                 ) : (
                   <p className="text-sm font-medium text-gray-900">{profile.interest || '-'}</p>
                 )}
              </div>
              
              <div className="col-span-full">
                 <label className="block text-xs font-medium text-gray-500 mb-1">จุดแข็ง (Strengths)</label>
                 {editingSection === 'social' ? (
                  <textarea
                    name="strengths"
                    value={formData.strengths || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                 ) : (
                   <p className="text-sm font-medium text-gray-900 whitespace-pre-line">{profile.strengths || '-'}</p>
                 )}
              </div>
              
              <div className="col-span-full">
                 <label className="block text-xs font-medium text-gray-500 mb-1">จุดอ่อน (Weaknesses)</label>
                 {editingSection === 'social' ? (
                  <textarea
                    name="weakness"
                    value={formData.weakness || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                 ) : (
                   <p className="text-sm font-medium text-gray-900 whitespace-pre-line">{profile.weakness || '-'}</p>
                 )}
              </div>
           </div>
      </ProfileSection>

      {/* 5. Additional Info */}
      <ProfileSection 
        title="ข้อมูลเพิ่มเติม" 
        icon={FiTrendingUp} 
        isEditing={editingSection === 'additional'}
        onEdit={() => handleEdit('additional')}
        onSave={handleSectionSave}
        onCancel={handleCancel}
        hideEdit={readOnly}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ยอดขายรายเดือน</label>
              {editingSection === 'additional' ? (
                <input
                  type="number"
                  name="monthlySales"
                  value={formData.monthlySales || ''}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {profile.monthlySales ? Number(profile.monthlySales).toLocaleString() : '-'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ยอดขายรายปี</label>
              {editingSection === 'additional' ? (
                <input
                  type="number"
                  name="yearlySales"
                  value={formData.yearlySales || ''}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {profile.yearlySales ? Number(profile.yearlySales).toLocaleString() : '-'}
                </p>
              )}
            </div>
             <div className="col-span-full">
               <label className="block text-xs font-medium text-gray-500 mb-1">บันทึกเพิ่มเติม</label>
               {editingSection === 'additional' ? (
                <textarea
                  name="note"
                  value={formData.note || ''}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
               ) : (
                 <p className="text-sm font-medium text-gray-900 whitespace-pre-line">{profile.note || '-'}</p>
               )}
            </div>
        </div>
      </ProfileSection>

      {/* 6. Policy Section (Dedicated) */}
      <ProfileSection
      title="กรมธรรม์" 
        icon={FiFileText}
        isEditing={editingSection === 'policy'}
        onEdit={() => setEditingSection('policy')}
        onCancel={() => setEditingSection(null)}
        // No save button for policy section as it manages its own state
        hideEdit={readOnly}
        customActions={editingSection === 'policy' && (
          <span className="text-xs text-gray-500 mr-2">กำลังแก้ไข...</span>
        )}
      >
        <PolicyManager
          agentId={profile.id}
          isEditable={editingSection === 'policy'}
          showTitle={true}
        />
      </ProfileSection>

      {/* 7. Training Section (Dedicated) */}
      <ProfileSection
       title="การฝึกอบรม"
        icon={FiBook}
        isEditing={editingSection === 'training'}
        onEdit={() => setEditingSection('training')}
        onCancel={() => setEditingSection(null)}
        // No save button for training section as it manages its own state
        hideEdit={readOnly} 
        customActions={editingSection === 'training' && (
           <span className="text-xs text-gray-500 mr-2">กำลังแก้ไข...</span>
        )}
      >
        <TrainingManager 
          agentId={profile.id} 
          isEditable={editingSection === 'training'} 
          showTitle={true}
        />
      </ProfileSection>

      {/* 8. Coaching Section (Dedicated) */}
      <ProfileSection
       title="การได้รับคำปรึกษา"
        icon={FiMessageSquare}
        isEditing={editingSection === 'coaching'}
        onEdit={() => setEditingSection('coaching')}
        onCancel={() => setEditingSection(null)}
        hideEdit={readOnly} 
        customActions={editingSection === 'coaching' && (
           <span className="text-xs text-gray-500 mr-2">กำลังแก้ไข...</span>
        )}
      >
        <CoachingManager 
          agentId={profile.id} 
          isEditable={editingSection === 'coaching'} 
          showTitle={true}
        />
      </ProfileSection>
      </div>
    </div>
  );
};

export default AgentProfileDetail;
